
/**
 * @typedef {Object} Selection
 * @property {boolean} self - select or unselect matching editor
 * @property {boolean} children - continue checking on editor children
 */

function ALL_MATCH(editor) {
	return {
		self: true,
		children: true
	};
}

/**
 * Base class for all data editors
 */
export class Editor {
	/**
	 * @param {Editor} parentEditor	parent editor or null if this editor has not parent editor (is root)
	 * @param {string} fieldPath	absolute name of this editor (e.g. invoice.address.latitude)
	 * @param {object} obj			nullable reference to a object from which the data will be obtained
	 * @param {string} member		nullable name of a property of the obj holding the data.
	 */
	constructor(parentEditor, fieldPath, obj, member) {
		this.parent = parentEditor;
		this.field = fieldPath;
		this.obj = obj;
		this.member = member;
		/** @type {function[]} */
		this.listeners = [];
		/** @type {boolean} */
		this.refreshDialogOnChange = false;
	}

	get data() {
		return this.obj === null ? null : this.obj[this.member];
	}

	set data(newValue) {
		if (this.obj === null || this.member === null) throw "Editor has not data object";
		const oldValue = this.obj[this.member];
		const wasChange = oldValue == null ? newValue != null : oldValue !== newValue;
		this.obj[this.member] = newValue;
		if (wasChange) this.wasChange(newValue);
	}

	get readOnly() {
		return false;
	}

	/**
	 * Change field name to a new value
	 * @param {string} field a new name in x.y.z notation
	 */
	rename(field) {
		this.field = field;
	}

	/**
	 * Return root element representing this editor on the screen. If the root element wasn't build yet, this getter
	 * will call rebuild() method
	 * Override this method to return root element of this editor.
	 * It can be used to insert editor's UI to DOM container.
	 */
	get ui() {
		if (this.root == null) this.rebuild();
		return this.root;
	}

	/**
	 * Completly rebuild its DOM UI and store the root element to this.root
	 */
	rebuild() {
		console.log('Editor '+this.constructor.name+' doesn\'t implement rebuild');
		this.root = null;
	}

	refreshDialog() {
		if (this.parent == null) throw "Unsatisfied call to refreshDialog()";
		this.parent.refreshDialog();
	}

	/**
	 * Convert from JSON representation to Widget rep.
	 */
	fromData(d) { return d; }

	/**
	 * Convert from widget's representation to JSON.
	 */
	toData(d) { return d; }

	/**
	 * Update screen (DOM) according to underlying data
	 */
	updateScreen() { }

	/**
	 * Update underlying data from screen (DOM)
	 */
	updateData() { }

	focus() {}

	/**
	 * Disable editing for the editor and its children
	 * @param {function} selector - predicate to further test if the editor will be disabled (default value disable all)
	 */
	disable(selector = ALL_MATCH) {
		const m = selector(this);
		if (m.self) this.enableSelf(false);
		if (m.children && this.children != null) {
			for (let ch of this.children) {
				ch.disable(selector);
			}
		}
	}


	enableSelf(enable) {
	}

	/**
	 * Enable editing for the editor and its children
	 * @param {function} selector - predicate to further test if the editor will be enabled (default value enable all)
	 */
	enable(selector = ALL_MATCH) {
		const m = selector(this);
		if (m.self) this.enableSelf(true);
		if (m.children && this.children != null) {
			for (let ch of this.children) {
				ch.enable(selector);
			}
		}
	}

	val(newValue) {
		this.data = newValue;
		this.updateScreen();
	}

	isValid() {
		return true;
	}


	/**
	 * Register listener for a data change.
	 * @param {function} handler	function called on change with arguments (object, memberName, newValue)
	 */
	addChangeListener(handler) {
		if (typeof handler === 'function') this.listeners.push(handler);
		else alert('Wrong change event listener '+handler);
	}

	fireChangeEvent(newValue) {
		this.onChange(null, newValue);
		for (const listener of this.listeners) listener(this.obj, this.member, newValue);
	}

	wasChange(newValue) {
		let target = this;
		let stopBubble;
		do {
			stopBubble = target.onChange(this.field, newValue);
			target = target.parent;
		} while (!stopBubble && target !== null);
	}

	onChange(field, newValue) {
		if (this.refreshDialogOnChange) {
			// Automatic refresh of the dialog
			setTimeout(function () {
				this.refreshDialog();
			}.bind(this), 0);
		}
		return false;
	}

	addChildEditor(child) {
		if (this.children == null) this.children = [];
		this.children.push(child);
		return child;
	}

	removeChildEditor(child) {
		if (this.children == null) return;
		for( let i = 0; i < this.children.length; i++){
			if ( this.children[i] === child) {
				this.children.splice(i, 1);
				return child;
			}
		}
		console.warn("Editor child not found for removal");
		return child;
	}

	displayError(problem) {
		this.problem = problem;
	}

	/**
	 * Return boolean if the targetted editor was found and the error displayed.
	 */
	displayErrorOnEditor(editorName, problem) {
		if (editorName === this.field) {
			this.displayError(problem);
			return true;
		} else if (this.children != null) {
			for (let ch of this.children) {
				const ok = ch.displayErrorOnEditor(editorName, problem);
				if (ok) return true;
			}
		}
		return false;
	}

	/**
	 * Remove error notifications on this editor
	 */
	clearError() {
		this.problem = null;
	}

	/**
	 * Remove error notifications on this editor and all its children editors
	 */
	clearErrors() {
		this.clearError();
		if (this.children != null) {
			for (let ch of this.children) ch.clearErrors();
		}
	}

	/**
	 * Find editor in the hierarchy of children and return it or null if nothing found
	 */
	find(fieldName) {
		if (fieldName === this.field) return this;
		else if (this.children != null) {
			for (let ch of this.children) {
				const found = ch.find(fieldName);
				if (found !== null) return found;
			}
		}
		return null;
	}
}
